import   React                     ,
       { useRef                    ,
         useState                  }  from 'react';

import   classNames                   from "classnames";

import   styles                       from "./StorageBreakdownPopover.module.scss";

import { getAdminStorageBreakdown  ,
         StorageBreakdown          }  from '../../../backendServices/adminManagment/api_storage';
import { formatStorageSize         }  from '../../../utils/utils_storage';

// Au survol de « Stockage » : de quoi est fait l'espace occupé, par grosses rubriques
// (vidéos, chaînes, enregistrements…). La mesure parcourt le disque côté serveur
// (~2 s sur le plus gros compte) : on ne la refait pas à chaque passage de souris.
const REFRESH_AFTER_MS = 15_000;

interface StorageBreakdownPopoverProps
 {
  adminId  : number | null    ;
  children : React.ReactNode  ;
 }

const taille = ( bytes: number ) => formatStorageSize ( bytes , new Date() );

// Sous 1 %, « < 1 % » plutôt qu'un « 0 % » qui laisserait croire la rubrique vide.
const pourcentage = ( bytes: number , total: number ): string => {
  if ( total <= 0 ) { return '—'; }
  const p = ( bytes / total ) * 100;
  return p > 0 && p < 1 ? '< 1 %' : `${Math.round(p)} %`;
};

const StorageBreakdownPopover = ( { adminId , children }: StorageBreakdownPopoverProps ) => {

  const [ open      , setOpen      ] = useState<boolean>(false);
  const [ loading   , setLoading   ] = useState<boolean>(false);
  const [ failed    , setFailed    ] = useState<boolean>(false);
  const [ breakdown , setBreakdown ] = useState<StorageBreakdown | null>(null);

  const lastFetchRef = useRef<number>(0);

  const handleEnter = async () => {

    setOpen(true);

    if ( adminId === null || loading || Date.now() - lastFetchRef.current < REFRESH_AFTER_MS ) { return; }

    lastFetchRef.current = Date.now();
    setLoading(true);

    const result = await getAdminStorageBreakdown ( adminId );

    setFailed ( result.code !== 1 || result.payload === null );
    if ( result.code === 1 && result.payload !== null ) { setBreakdown ( result.payload ); }
    setLoading(false);
  };

  const total = breakdown?.measuredBytes ?? 0;
  // Le compteur suit le disque à chaque geste ; un écart d'un Mo ou plus est une dérive
  // que le recalcul des 6 h rattrapera — autant le dire plutôt que laisser deux chiffres.
  const drift = breakdown !== null && Math.abs ( breakdown.quotaUsed - breakdown.measuredBytes ) >= 1024 * 1024;

  return (
    <div className      = {classNames(styles.wrapper)}
         onMouseEnter   = {handleEnter}
         onMouseLeave   = {() => setOpen(false)}>

      {children}

      {open && (
        <div className={classNames(styles.popover)}>

          <div className={classNames(styles.title)}>Détail du stockage</div>

          {breakdown === null && loading && <div className={classNames(styles.muted)}>Calcul…</div>}
          {breakdown === null && !loading && failed && <div className={classNames(styles.muted)}>Détail indisponible</div>}

          {breakdown !== null && breakdown.items.length === 0 && <div className={classNames(styles.muted)}>Aucun fichier</div>}

          {breakdown !== null && breakdown.items.map ( (item) => (
            <div key={item.key} className={classNames(styles.row)}>
              <div className={classNames(styles.label)}>{item.label}</div>
              <div className={classNames(styles.size)}>{taille(item.bytes)} ({pourcentage(item.bytes, total)})</div>
            </div>
          ))}

          {breakdown !== null && (
            <div className={classNames(styles.total)}>
              <div>Total</div>
              <div>{taille(total)}</div>
            </div>
          )}

          {drift && breakdown !== null && (
            <div className={classNames(styles.muted)}>
              Compteur à {taille(breakdown.quotaUsed)} : il sera recalé au prochain recalcul (toutes les 6 h).
            </div>
          )}

          {loading && breakdown !== null && <div className={classNames(styles.muted)}>Mise à jour…</div>}
        </div>
      )}
    </div>
  );
};

export default StorageBreakdownPopover;
